/**
 * @fileoverview Pure helpers that describe every filter currently applied to a
 *   DataGrid — toolbar filter values, per-column filter-row values, the global
 *   search text, and the query builder — as render-ready chip descriptors.
 * @module ui-admin/DataGrid/filterChips
 *
 * Responsibilities:
 * - Build the active-filter chip list shown in the grid's filter status bar.
 * - Count query-builder conditions for the Query button badge.
 * - Format filter values (resolve select/multi-select option labels).
 *
 * Dependencies:
 * - types: DataGridColumn, DataGridFilter, QueryGroup, GridState.
 *
 * Architecture:
 * - Presentation-layer pure functions. No React, no storage, no fetching —
 *   DataGrid renders the chips and owns clearing them.
 *
 * Notes:
 * - Emptiness rules mirror DataGrid's own filtering: null, '' and empty arrays
 *   are NOT applied filters and produce no chip.
 * - Used by every grid in tenant-web/system-web via the shared DataGrid, so a
 *   change here applies to all grids at once.
 */
import type { DataGridColumn, DataGridFilter, GridState, QueryCondition, QueryGroup } from './types';
import { isQueryGroup } from './types';

/**
 * @contract ActiveFilterChip
 * Purpose: One render-ready descriptor for a filter currently narrowing the grid.
 * Fields:  key (stable React key), kind (which mechanism applied it), label
 *          (mechanism/field name), value (formatted user-facing value),
 *          targetId (filter/column id to clear — set for 'filter' and 'column').
 * Rules:   Only applied (non-empty) filters produce chips; `value` is always a
 *          non-empty display string.
 * Sync:    None — derived purely from live grid state each render.
 */
export interface ActiveFilterChip {
  key: string;
  kind: 'search' | 'filter' | 'column' | 'query';
  label: string;
  value: string;
  targetId?: string;
}

/**
 * @process countQueryConditions
 * Steps: 1. Return 0 for a missing/empty query.
 *        2. Recursively count leaf conditions inside nested groups.
 * Inputs: query — QueryGroup or undefined.
 * Outputs: Total leaf condition count.
 * Failure Handling: malformed items are ignored (never throws).
 * Idempotency: REQUIRED (pure).
 */
export function countQueryConditions(query: QueryGroup | undefined): number {
  if (!query) return 0;
  let count = 0;
  for (const item of query.conditions) {
    if (isQueryGroup(item)) count += countQueryConditions(item);
    else count += 1;
  }
  return count;
}

/**
 * @process formatFilterChipValue
 * Steps: 1. Resolve option labels for select/status/multi-select filters.
 *        2. Join array values with ", "; stringify everything else.
 * Inputs: filter definition (may be undefined), raw filter value.
 * Outputs: A human-readable display string.
 * Failure Handling: null/undefined → ''.
 * Idempotency: REQUIRED (pure).
 */
export function formatFilterChipValue(filter: DataGridFilter | undefined, value: unknown): string {
  if (value == null) return '';
  const usesOptions = filter?.type === 'select' || filter?.type === 'status' || filter?.type === 'multi-select';
  if (usesOptions && filter?.options) {
    const rawList = Array.isArray(value) ? value : [value];
    return rawList
      .map((v) => filter.options!.find((opt) => opt.value === v)?.label ?? String(v))
      .join(', ');
  }
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  return String(value);
}

function isAppliedFilterValue(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/**
 * @process buildActiveFilterChips
 * Steps: 1. Chip for the global search text when non-empty.
 *        2. Chip per applied toolbar filter value (label + formatted value).
 *        3. Chip per applied per-column filter-row value (column header).
 *        4. One chip for the query builder when it has conditions.
 * Inputs: searchText, filterValues, filters (toolbar filter defs),
 *         columnFilters, columns, activeQuery.
 * Outputs: ActiveFilterChip[] in stable order: search, filters, columns, query.
 * Failure Handling: unknown filter/column ids fall back to the raw id.
 * Idempotency: REQUIRED (pure).
 */
export function buildActiveFilterChips(params: {
  searchText?: string;
  filterValues?: GridState['filterValues'];
  filters?: DataGridFilter[];
  columnFilters?: GridState['columnFilters'];
  columns?: DataGridColumn<any>[];
  activeQuery?: QueryGroup;
}): ActiveFilterChip[] {
  const { searchText = '', filterValues = {}, filters = [], columnFilters = [], columns = [], activeQuery } = params;
  const chips: ActiveFilterChip[] = [];

  if (searchText.trim() !== '') {
    chips.push({ key: 'search', kind: 'search', label: 'Search', value: searchText.trim() });
  }

  for (const [filterId, value] of Object.entries(filterValues)) {
    if (!isAppliedFilterValue(value)) continue;
    const filterDef = filters.find((f) => f.id === filterId);
    const formatted = formatFilterChipValue(filterDef, value);
    if (formatted === '') continue;
    chips.push({
      key: `filter-${filterId}`,
      kind: 'filter',
      label: filterDef?.label ?? filterId,
      value: formatted,
      targetId: filterId,
    });
  }

  for (const cf of columnFilters) {
    if (!isAppliedFilterValue(cf.value)) continue;
    const col = columns.find((c) => c.id === cf.id);
    const header = col && typeof col.header === 'string' ? col.header : cf.id;
    chips.push({
      key: `column-${cf.id}`,
      kind: 'column',
      label: header,
      value: formatFilterChipValue(filters.find((f) => f.id === cf.id), cf.value),
      targetId: cf.id,
    });
  }

  const conditionCount = countQueryConditions(activeQuery);
  if (conditionCount > 0) {
    chips.push({
      key: 'query',
      kind: 'query',
      label: 'Query',
      value: `${conditionCount} condition${conditionCount === 1 ? '' : 's'}`,
    });
  }

  return chips;
}

/**
 * @process countAppliedGridFilters
 * Steps: 1. Count applied toolbar filter values + per-column filter-row values.
 * Inputs: filterValues, columnFilters.
 * Outputs: Number shown as the Filters button badge (0 hides the badge).
 * Failure Handling: none (pure).
 * Idempotency: REQUIRED (pure).
 */
export function countAppliedGridFilters(
  filterValues: GridState['filterValues'] = {},
  columnFilters: GridState['columnFilters'] = [],
): number {
  let count = 0;
  for (const value of Object.values(filterValues)) {
    if (isAppliedFilterValue(value)) count += 1;
  }
  for (const cf of columnFilters) {
    if (isAppliedFilterValue(cf.value)) count += 1;
  }
  return count;
}

/**
 * @process summarizeQueryCondition
 * Steps: 1. Render one leaf condition as "field operator value" for tooltips.
 * Inputs: condition, columns (for header labels).
 * Outputs: Short display string.
 * Failure Handling: unknown field → raw field id.
 * Idempotency: REQUIRED (pure).
 */
export function summarizeQueryCondition(
  condition: QueryCondition,
  columns: DataGridColumn<any>[] = [],
): string {
  const col = columns.find((c) => c.id === condition.field);
  const field = col && typeof col.header === 'string' ? col.header : condition.field;
  const op = condition.operator;
  const needsValue = op !== 'isEmpty' && op !== 'isNotEmpty';
  return needsValue ? `${field} ${op} ${condition.value}` : `${field} ${op}`;
}

/**
 * @process summarizeQueryGroup
 * Steps: 1. Join condition summaries with the group logic (AND/OR).
 *        2. Wrap nested groups in parentheses.
 * Inputs: query group, columns (for header labels).
 * Outputs: One-line query summary for the query chip tooltip.
 * Failure Handling: empty group → ''.
 * Idempotency: REQUIRED (pure).
 */
export function summarizeQueryGroup(
  group: QueryGroup,
  columns: DataGridColumn<any>[] = [],
): string {
  return group.conditions
    .map((item) => (isQueryGroup(item) ? `(${summarizeQueryGroup(item, columns)})` : summarizeQueryCondition(item, columns)))
    .join(` ${group.logic} `);
}
